/* eslint-disable @typescript-eslint/no-empty-function */
import { animate, keyframes, state, style, transition, trigger } from '@angular/animations';
import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, Output, SimpleChange, ViewChild, ViewEncapsulation, } from '@angular/core';
import { ButtonPurpose } from '@mhe/ngx-shared/button';
import { TemplateUuids, getFocusableElements, isElementFocusable, provideValueAccessor, } from '@mhe/ngx-shared/utils';
import { differenceWith, isEqual, uniqBy } from 'lodash-es';
import { Subject, asyncScheduler, fromEvent, merge } from 'rxjs';
import { subscribeOn, takeUntil, tap } from 'rxjs/operators';
import dropdownPositions from './dropdown-positions';
import * as i0 from "@angular/core";
import * as i1 from "@mhe/ngx-shared/translate";
import * as i2 from "@angular/cdk/overlay";
import * as i3 from "./dropdown.service";
import * as i4 from "@angular/common";
import * as i5 from "@angular/cdk/portal";
import * as i6 from "@angular/router";
// const DEFAULT_BUTTON_WIDTH = 100;
const DEFAULT_MENU_WIDTH = 250;
const DROPDOWN_SELECTED_STATE_TIME = 250;
export class DropdownComponent {
    _changeDetectorRef;
    translateService;
    overlay;
    dropdownService;
    minWidth = 0;
    maxWidth = 250;
    splitActionText = null;
    disabled = false;
    image;
    optionPosition = 'left';
    toggleButtonAriaLabel = '';
    listboxLabel;
    selected;
    isMenu;
    icon = '';
    multiple = false;
    filterable = false;
    syncWidths = false;
    buttonWidth;
    searchable = false;
    selectAll = true;
    selectAllText = 'Select all';
    placeholder = 'Select';
    panelClass = '';
    closeOnBlur = false;
    /** Flattened list of all options */
    _flatOptions = [];
    /** Filtered list of options to display */
    _filteredOptions = [];
    /** Tracks which options are nested */
    _isChild;
    /** Search term for filtering options */
    _searchTerm = '';
    get options() {
        return this._filteredOptions;
    }
    set options(options) {
        this._isChild = [];
        this._flatOptions = (options ?? []).reduce((acc, cur) => {
            this._isChild.push(false);
            const children = cur.children ?? [];
            for (const _c of children) {
                this._isChild.push(true);
            }
            return [...acc, cur, ...children];
        }, []);
        this._filteredOptions = this.filterOptions(this._searchTerm);
        this.checkSelectAll();
    }
    // value
    get value() {
        return this.selected ?? [];
    }
    set value(value) {
        this.selected = value;
    }
    selectionChange = new EventEmitter();
    searchTerm = new EventEmitter();
    scrollBottomReached = new EventEmitter();
    // eslint-disable-next-line @angular-eslint/no-output-native
    close = new EventEmitter();
    splitActionClick = new EventEmitter();
    controlButton;
    splitActionButton;
    toggleButtonWithIcon;
    dropdownMenuPortal;
    doc;
    destroy$ = new Subject();
    // expose import to template
    _buttonPurpose = ButtonPurpose;
    allSelected = false;
    focusedOption = null;
    isExpanded = false;
    listItems;
    overlayRef;
    searchIsFocused = false;
    selectedSuffix = 'selected';
    templateUuids = new TemplateUuids();
    triggerHideDropdown = false;
    triggerOptionSelectionAnimation = false;
    openedEventSubscription;
    /** miscl. getters */
    get currentSelection() {
        return this.getDropdownDisplay(this.selected, this.placeholder);
    }
    // TODO: this smells like an anti-pattern
    get buttonId() {
        return this.templateUuids.get('buttonId');
    }
    get hasIcon() {
        return this.icon !== '';
    }
    constructor(_changeDetectorRef, translateService, overlay, dropdownService, _doc) {
        this._changeDetectorRef = _changeDetectorRef;
        this.translateService = translateService;
        this.overlay = overlay;
        this.dropdownService = dropdownService;
        this.doc = _doc;
    }
    ngOnInit() {
        this.translateService.translate('ngx-shared-selected').subscribe((value) => {
            this.selectedSuffix = value;
        });
        this.openedEventSubscription = this.dropdownService.opened.subscribe((dropdown) => {
            // If the opened event was sent by this instance, don't close the dropdown.
            if (dropdown !== this && this.isExpanded) {
                this.hideDropdown();
            }
        });
    }
    ngOnDestroy() {
        this.hideDropdown(true);
        this.destroy$.next();
        this.destroy$.unsubscribe();
        if (this.openedEventSubscription) {
            this.openedEventSubscription.unsubscribe();
        }
    }
    ngOnChanges(changes) {
        if (changes.options) {
            const options = changes.options.currentValue ?? [];
            if (uniqBy(options, 'value').length !== options.length) {
                throw new Error('options must have unique values');
            }
        }
        if (changes.selected) {
            const selected = changes.selected.currentValue ?? [];
            if (!this.multiple && selected.length > 1) {
                throw new Error('selected must contain only one element unless multiple is true');
            }
            this.checkSelectAll();
        }
    }
    createDropdownPortal() {
        const optionPosition = this.splitActionText ? 'right' : this.optionPosition;
        this.overlayRef = this.overlay.create({
            positionStrategy: this.getPositionStrategy(optionPosition),
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
            width: this.width(),
            maxHeight: 250,
            panelClass: this.panelClass,
        });
    }
    getPositionStrategy(optionPosition) {
        const { bottom_left, bottom_right, top_left, top_right } = dropdownPositions;
        const positions = optionPosition === 'right' ? [bottom_right, top_right] : [bottom_left, top_left];
        // Flexible position strategy bounds dropdown by window and allows to grow to maximize possible height
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.controlButton || this.splitActionButton || this.toggleButtonWithIcon)
            .withFlexibleDimensions(true)
            .withGrowAfterOpen(true)
            .withPush(false)
            .withPositions([...positions]);
        return positionStrategy;
    }
    showDropdown() {
        this.createDropdownPortal();
        if (this.dropdownMenuPortal) {
            this.overlayRef.attach(this.dropdownMenuPortal);
            this.listItems = Array.from(this.overlayRef.hostElement.querySelectorAll('li[role="presentation"]'));
            const events = [fromEvent(this.doc, 'click')];
            if (this.closeOnBlur) {
                events.push(fromEvent(window, 'blur'));
            }
            merge(...events)
                .pipe(tap((event) => {
                this.onClickedOutside(event);
            }), subscribeOn(asyncScheduler), takeUntil(this.overlayRef.detachments()))
                .subscribe();
        }
    }
    hideDropdown(returnFocus = true) {
        this.triggerHideDropdown = false;
        if (this.overlayRef?.hasAttached()) {
            this.overlayRef.detach();
            this.overlayRef.dispose();
            if (returnFocus) {
                this.focusControlButton();
            }
        }
        this.isExpanded = false;
        this._changeDetectorRef.detectChanges();
        this.close.emit();
        if (this.isMenu) {
            this.resetSelectedState();
        }
    }
    resetSelectedState() {
        this.selected = [];
    }
    width() {
        const elementRef = this.controlButton || this.splitActionButton || this.toggleButtonWithIcon;
        const clientRect = elementRef.nativeElement.getBoundingClientRect();
        let width = clientRect.width;
        if (this.syncWidths) {
            return width;
        }
        if (width < DEFAULT_MENU_WIDTH) {
            width = 'auto';
        }
        if (width > DEFAULT_MENU_WIDTH) {
            width = DEFAULT_MENU_WIDTH;
        }
        return width;
    }
    getContext(i, option) {
        return { isChild: this._isChild[i], option };
    }
    containsOption(target, options) {
        return options?.some((option) => option.value === target.value);
    }
    selectOption(selectedOption) {
        if (selectedOption.disabled || !!selectedOption.children) {
            return;
        }
        if (!this.multiple) {
            this.selected = [selectedOption];
            this.triggerOptionSelectionAnimation = true;
        }
        else {
            if (this.containsOption(selectedOption, this.selected)) {
                this.selected = this.selected?.filter((option) => option.value !== selectedOption.value);
            }
            else {
                this.selected = (this.selected ?? []).concat(selectedOption);
            }
        }
        this.updateDropdown();
    }
    optionSelectionAnimationDone(event) {
        if (event.toState === 'selected') {
            this.triggerHideDropdown = true;
            this.triggerOptionSelectionAnimation = false;
        }
    }
    hideDropdownAnimationDone(event) {
        if (event.toState === 'dropdownHidden') {
            this.hideDropdown();
        }
    }
    selectAllOptions() {
        if (this.allSelected) {
            this.selected = [];
        }
        else {
            this.selected = [...this.options];
        }
        this.updateDropdown();
    }
    checkSelectAll() {
        if (this.selected?.length && this.options.length === this.selected.length) {
            this.allSelected = !differenceWith(this.options, this.selected, isEqual).length;
        }
        else {
            this.allSelected = false;
        }
    }
    updateDropdown() {
        this.checkSelectAll();
        this.selectionChange.emit(this.selected);
        this._onChange(this.selected);
    }
    getDropdownDisplay(selectedOptions, placeholder) {
        const selectedLength = selectedOptions?.length;
        if (!selectedLength) {
            return placeholder;
        }
        if (selectedLength === 1) {
            return selectedOptions[0].viewValue;
        }
        return `${selectedOptions.length} ${this.selectedSuffix}`;
    }
    hasPlaceholderText(selectedOptions) {
        return selectedOptions?.length === 0;
    }
    linkClickHandler(e, option) {
        if (!(option.href || option.routerLink) || option.disabled) {
            e.preventDefault();
        }
    }
    linkFocusHandler(event, option) {
        const dropdownItem = event.target.parentElement;
        if (option.disabled) {
            return;
        }
        this.listItems.forEach((listItem) => {
            listItem.classList.remove('focused');
        });
        dropdownItem.classList.add('focused');
    }
    toggleMenu(event) {
        event.preventDefault();
        event.stopPropagation();
        this.isExpanded = !this.isExpanded;
        if (this.isExpanded) {
            this.dropdownService.emitOpenEvent(this);
            this.showDropdown();
        }
        else {
            this.hideDropdown();
        }
        if (this.isExpanded && !this.multiple) {
            const initialSelection = this.getInitialSelectionLink();
            this.focusedOption = initialSelection;
            if (initialSelection) {
                setTimeout(() => {
                    initialSelection.focus();
                }, 0);
            }
        }
        else {
            this.focusControlButton();
        }
    }
    focusControlButton() {
        setTimeout(() => {
            if (this.hasIcon && this.toggleButtonWithIcon) {
                this.toggleButtonWithIcon.nativeElement.focus();
            }
            else if (this.splitActionText && this.splitActionButton) {
                this.splitActionButton.nativeElement.focus();
            }
            else if (this.controlButton) {
                this.controlButton.nativeElement.focus();
            }
        }, 0);
    }
    get dropdownButton() {
        const button = this.controlButton || this.toggleButtonWithIcon || this.splitActionButton;
        return button.nativeElement;
    }
    getInitialSelectionLink() {
        if (!this.listItems || this.listItems.length === 0) {
            return undefined;
        }
        let item = this.listItems[0];
        if (this.selected?.length) {
            const selectedValue = this.selected[0].value;
            this.listItems.forEach((listitem, index) => {
                const option = this._filteredOptions[index];
                if (option && option.value === selectedValue) {
                    item = listitem;
                }
            });
        }
        return item.querySelector('.dropdown-item');
    }
    incrementSelectionLink() {
        const currIdx = this.listItems.findIndex((e, i) => e.classList.contains('focused'));
        let focusedOption;
        for (let i = currIdx + 1; i < this.listItems.length; i++) {
            const currentListItem = this.listItems[i];
            if (currentListItem.classList.contains('disabled') ||
                currentListItem.classList.contains('option-group-title')) {
                continue;
            }
            else {
                focusedOption = this.listItems[i].querySelector('.dropdown-item');
                break;
            }
        }
        if (focusedOption) {
            this.focusedOption = focusedOption;
            this.focusedOption.focus();
        }
    }
    decrementSelectionLink() {
        const currIdx = this.listItems.findIndex((e, i) => e.classList.contains('focused'));
        let focusedOption;
        for (let i = currIdx - 1; i >= 0; i--) {
            const currentListItem = this.listItems[i];
            if (currentListItem.classList.contains('disabled') ||
                currentListItem.classList.contains('option-group-title')) {
                continue;
            }
            else {
                focusedOption = currentListItem.querySelector('.dropdown-item');
                break;
            }
        }
        if (focusedOption) {
            this.focusedOption = focusedOption;
            this.focusedOption.focus();
        }
    }
    onKey(event) {
        switch (event.key) {
            case 'Escape':
                event.stopPropagation();
                this.hideDropdown(true);
                break;
            case 'Tab':
                event.preventDefault();
                this.hideDropdown(false);
                this.tabToNextFocusable();
                break;
            case ' ':
            case 'Enter':
                if (this.isExpanded && !this.searchIsFocused) {
                    this.focusedOption.click();
                    event.preventDefault();
                }
                break;
            case 'ArrowDown':
                this.incrementSelectionLink();
                event.preventDefault();
                break;
            case 'ArrowUp':
                this.decrementSelectionLink();
                event.preventDefault();
                break;
        }
    }
    tabToNextFocusable() {
        const focusableElems = getFocusableElements();
        const dropdownButtonIndex = focusableElems.findIndex((element) => element === this.dropdownButton);
        if (dropdownButtonIndex !== -1) {
            let nextIndex = dropdownButtonIndex + 1;
            if (nextIndex > focusableElems.length) {
                nextIndex = 0;
            }
            const nextFocusable = focusableElems[nextIndex];
            nextFocusable.focus();
        }
    }
    onClickedOutside(event) {
        if (!this.overlayRef?.overlayElement?.contains(event?.target)) {
            this.hideDropdown(false);
            if (!isElementFocusable(document.activeElement)) {
                this.focusControlButton();
            }
        }
    }
    disabledProp(disabled) {
        return disabled ? '' : null;
    }
    getOptionLabel(option) {
        const optionValueList = [option.viewValue];
        if (option.viewDescription) {
            optionValueList.push(option.viewDescription);
        }
        for (const i in option.viewIcons) {
            if (optionValueList) {
                optionValueList.push(option.viewIcons[i].label);
            }
        }
        return optionValueList.join(' ');
    }
    _onChange = (_) => { };
    _onTouch = () => { };
    registerOnChange(fn) {
        this._onChange = fn;
    }
    registerOnTouched(fn) {
        this._onTouch = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this._changeDetectorRef.markForCheck();
    }
    writeValue(val) {
        if (val !== this.selected) {
            const selectedChange = new SimpleChange(this.selected, val, true);
            this.selected = val;
            this.ngOnChanges({ selected: selectedChange });
            this._changeDetectorRef.markForCheck();
        }
    }
    onScroll(event) {
        if (event.target.scrollHeight - event.target.scrollTop < event.target.offsetHeight) {
            this.scrollBottomReached.emit(event);
        }
    }
    searchOptions(event) {
        const { target } = event;
        this._searchTerm = target.value;
        if (this.searchable) {
            this.searchTerm.emit(this._searchTerm);
        }
        else {
            this._filteredOptions = this.filterOptions(this._searchTerm);
        }
        this.checkSelectAll();
    }
    trackById(index, item) {
        return item.value;
    }
    // currently, searchTerm filters on leaf nodes only
    filterOptions(searchTerm) {
        const options = [...this._flatOptions];
        if (!searchTerm.trim().length) {
            return options;
        }
        return options.filter((option) => {
            if (option.children?.length) {
                return false;
            }
            else {
                return option.viewValue.toLowerCase().includes(searchTerm.trim().toLowerCase());
            }
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: DropdownComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i1.TranslateService }, { token: i2.Overlay }, { token: i3.DropdownService }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: DropdownComponent, selector: "mhe-dropdown", inputs: { minWidth: "minWidth", maxWidth: "maxWidth", splitActionText: "splitActionText", disabled: "disabled", image: "image", optionPosition: "optionPosition", toggleButtonAriaLabel: "toggleButtonAriaLabel", listboxLabel: "listboxLabel", selected: "selected", isMenu: "isMenu", icon: "icon", multiple: "multiple", filterable: "filterable", syncWidths: "syncWidths", buttonWidth: "buttonWidth", searchable: "searchable", selectAll: "selectAll", selectAllText: "selectAllText", placeholder: "placeholder", panelClass: "panelClass", closeOnBlur: "closeOnBlur", options: "options", value: "value" }, outputs: { selectionChange: "selectionChange", searchTerm: "searchTerm", scrollBottomReached: "scrollBottomReached", close: "close", splitActionClick: "splitActionClick" }, providers: [provideValueAccessor(DropdownComponent)], viewQueries: [{ propertyName: "controlButton", first: true, predicate: ["controlButton"], descendants: true }, { propertyName: "splitActionButton", first: true, predicate: ["splitActionButton"], descendants: true }, { propertyName: "toggleButtonWithIcon", first: true, predicate: ["toggleButtonWithIcon"], descendants: true }, { propertyName: "dropdownMenuPortal", first: true, predicate: ["dropdownMenuPortal"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<div\n  class=\"dropdown ahe-ui-dropdown\"\n  [class.btn-group]=\"splitActionText\"\n  (click)=\"_onTouch()\"\n  (keyup)=\"_onTouch()\"\n>\n  <ng-container *ngIf=\"!splitActionText; else splitButton\">\n    <button\n      *ngIf=\"!icon && !image; else iconButton\"\n      [id]=\"templateUuids.get('buttonId')\"\n      type=\"button\"\n      [attr.disabled]=\"disabledProp(disabled)\"\n      [attr.aria-expanded]=\"isExpanded\"\n      class=\"btn btn-secondary dropdown-btn\"\n      [ngClass]=\"{ placeholder: hasPlaceholderText(selected), open: isExpanded }\"\n      (click)=\"toggleMenu($event)\"\n      [ngStyle]=\"{ 'width.px': buttonWidth }\"\n      #controlButton\n    >\n      <span class=\"sr-only\">{{ toggleButtonAriaLabel }}</span>\n      <span>{{ currentSelection }}</span>\n      <i class=\"dpg-icon dpg-icon-dropdown-arrow-down\"></i>\n    </button>\n    <ng-template #iconButton>\n      <button\n        [id]=\"templateUuids.get('buttonId')\"\n        type=\"button\"\n        class=\"btn btn-icon ngx-shared\"\n        [ngClass]=\"{ 'btn-padding': image }\"\n        (click)=\"toggleMenu($event)\"\n        [attr.disabled]=\"disabledProp(disabled)\"\n        aria-haspopup=\"listbox\"\n        [attr.aria-expanded]=\"isExpanded\"\n        [ngStyle]=\"{ 'width.px': 36 }\"\n        #toggleButtonWithIcon\n      >\n        <span class=\"sr-only\">{{ toggleButtonAriaLabel }}</span>\n        <img *ngIf=\"image\" class=\"ahe-icon avatar-image\" [src]=\"image\" />\n        <i *ngIf=\"icon && !image\" class=\"ahe-icon {{ icon }}\"></i>\n      </button>\n    </ng-template>\n  </ng-container>\n</div>\n\n<ng-template cdkPortal #dropdownMenuPortal=\"cdkPortal\">\n  <div\n    class=\"ahe-ui-dropdown\"\n    [@hideDropdown]=\"!multiple && triggerHideDropdown ? 'dropdownHidden' : ''\"\n    (@hideDropdown.done)=\"hideDropdownAnimationDone($event)\"\n  >\n    <ul\n      role=\"listbox\"\n      (scroll)=\"onScroll($event)\"\n      (keydown)=\"onKey($event)\"\n      [attr.aria-expanded]=\"isExpanded\"\n      [attr.aria-label]=\"listboxLabel\"\n      class=\"dropdown-menu list-items-container\"\n      [class.dropdown-menu-right]=\"optionPosition === 'left'\"\n    >\n      <li *ngIf=\"filterable || searchable\" class=\"search-field\">\n        <div class=\"ahe-ui form-group dropdown-menu-search\">\n          <input\n            type=\"text\"\n            autocomplete=\"off\"\n            class=\"form-control search\"\n            (blur)=\"searchIsFocused = false\"\n            (focus)=\"searchIsFocused = true\"\n            placeholder=\"{{ 'ngx-shared-search' | mheTranslate | async }}\"\n            (keyup)=\"searchOptions($event)\"\n          />\n        </div>\n      </li>\n      <li *ngIf=\"multiple && selectAll\" (click)=\"selectAllOptions()\" role=\"option\">\n        <label\n          class=\"ahe-ui-checkbox dropdown-item\"\n          attr.aria-label=\"{{ 'ngx-shared-select-all' | mheTranslate | async }} {{\n            'ngx-shared-menu-option' | mheTranslate | async\n          }}\"\n        >\n          <input type=\"checkbox\" role=\"menuitemcheckbox\" [checked]=\"allSelected\" />\n          <span aria-hidden=\"true\">{{ 'ngx-shared-select-all' | mheTranslate | async }}</span>\n        </label>\n      </li>\n      <li\n        *ngFor=\"let option of options; trackBy: trackById; let i = index\"\n        #listItem\n        role=\"presentation\"\n        [attr.aria-disabled]=\"option.disabled\"\n        [class.checkmark]=\"!multiple && containsOption(option, selected)\"\n        [class.disabled]=\"option.disabled\"\n        [class.dpg-icon-confirmation]=\"!multiple && containsOption(option, selected)\"\n        [class.option-group-options]=\"_isChild[i]\"\n        [class.option-group-title]=\"!!option.children\"\n        (click)=\"selectOption(option)\"\n      >\n        <ng-container *ngTemplateOutlet=\"optionTemplate; context: getContext(i, option)\"></ng-container>\n      </li>\n    </ul>\n  </div>\n</ng-template>\n\n<ng-template\n  #optionContent\n  let-viewValue=\"viewValue\"\n  let-viewIcons=\"viewIcons\"\n  let-viewDescription=\"viewDescription\"\n>\n  <span aria-hidden=\"true\" [ngClass]=\"{ 'view-title': viewDescription }\">\n    <span>{{ viewValue }}</span>\n    <span class=\"view-icons\">\n      <i\n        *ngFor=\"let viewIcon of viewIcons\"\n        class=\"dpg-icon {{ viewIcon.class }}\"\n        title=\"{{ viewIcon.label }}\"\n      ></i>\n    </span>\n  </span>\n  <p class=\"view-description\" *ngIf=\"viewDescription\" aria-hidden=\"true\">{{ viewDescription }}</p>\n</ng-template>\n\n<ng-template #optionTemplate let-option=\"option\" let-isChild=\"isChild\" let-isHeader=\"option.children\">\n  <label *ngIf=\"multiple\" [attr.aria-label]=\"getOptionLabel(option)\" class=\"ahe-ui-checkbox dropdown-item\">\n    <input\n      type=\"checkbox\"\n      role=\"menuitemcheckbox\"\n      [value]=\"option.value\"\n      [checked]=\"containsOption(option, selected)\"\n    />\n    <ng-container *ngTemplateOutlet=\"optionContent; context: option\"></ng-container>\n  </label>\n  <ng-container *ngIf=\"!multiple\">\n    <a\n      *ngIf=\"(!option.routerLink && !option.href) || option.disabled\"\n      href\n      role=\"option\"\n      [attr.aria-label]=\"getOptionLabel(option)\"\n      [@optionSelection]=\"\n        this.triggerOptionSelectionAnimation && containsOption(option, selected) ? 'selected' : ''\n      \"\n      (@optionSelection.done)=\"optionSelectionAnimationDone($event)\"\n      class=\"dropdown-item\"\n      [class.is-header]=\"isHeader || null\"\n      [attr.tabindex]=\"!option.disabled && !option.children ? 0 : null\"\n      (click)=\"linkClickHandler($event, option)\"\n      (focus)=\"linkFocusHandler($event, option)\"\n    >\n      <ng-container *ngTemplateOutlet=\"optionContent; context: option\"></ng-container>\n    </a>\n    <a\n      *ngIf=\"option.href\"\n      [@optionSelection]=\"\n        this.triggerOptionSelectionAnimation && containsOption(option, selected) ? 'selected' : ''\n      \"\n      (@optionSelection.done)=\"optionSelectionAnimationDone($event)\"\n      class=\"dropdown-item ahe-ui-link\"\n      [class.is-header]=\"isHeader || null\"\n      (click)=\"linkClickHandler($event, option)\"\n      (focus)=\"linkFocusHandler($event, option)\"\n      href=\"{{ option.href }}\"\n      target=\"{{ option.target }}\"\n    >\n      <ng-container *ngTemplateOutlet=\"optionContent; context: option\"></ng-container>\n    </a>\n    <a\n      *ngIf=\"option.routerLink\"\n      [@optionSelection]=\"\n        this.triggerOptionSelectionAnimation && containsOption(option, selected) ? 'selected' : ''\n      \"\n      (@optionSelection.done)=\"optionSelectionAnimationDone($event)\"\n      class=\"dropdown-item ahe-ui-link\"\n      [class.is-header]=\"isHeader || null\"\n      (click)=\"linkClickHandler($event, option)\"\n      (focus)=\"linkFocusHandler($event, option)\"\n      routerLinkActive=\"active\"\n      routerLink=\"{{ option.routerLink }}\"\n    >\n      <ng-container *ngTemplateOutlet=\"optionContent; context: option\"></ng-container>\n    </a>\n  </ng-container>\n</ng-template>\n\n<ng-template #splitButton>\n  <button\n    [id]=\"templateUuids.get('buttonId')\"\n    class=\"btn btn-secondary\"\n    (click)=\"splitActionClick.emit($event)\"\n    #splitActionButton\n  >\n    {{ splitActionText }}\n  </button>\n  <button\n    type=\"button\"\n    [attr.disabled]=\"disabledProp(disabled)\"\n    [attr.aria-expanded]=\"isExpanded\"\n    class=\"btn btn-icon dpg-input-group-btn\"\n    [ngClass]=\"{ placeholder: hasPlaceholderText(selected), open: isExpanded }\"\n    (click)=\"toggleMenu($event)\"\n    #controlButton\n  >\n    <i class=\"dpg-icon dpg-icon-dropdown-arrow-down\">\n      <span class=\"sr-only\">{{ toggleButtonAriaLabel }}</span>\n    </i>\n  </button>\n</ng-template>\n", styles: ["mhe-dropdown{display:inline-block}.btn-padding{width:36px !important;padding:0 !important}.avatar-image{width:35px;height:33px;border-radius:4px}\n"], directives: [{ type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i4.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i4.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { type: i5.CdkPortal, selector: "[cdkPortal]", exportAs: ["cdkPortal"] }, { type: i4.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i4.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }, { type: i6.RouterLinkWithHref, selector: "a[routerLink],area[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "preserveFragment", "skipLocationChange", "replaceUrl", "state", "relativeTo", "routerLink"] }, { type: i6.RouterLinkActive, selector: "[routerLinkActive]", inputs: ["routerLinkActiveOptions", "routerLinkActive"], outputs: ["isActiveChange"], exportAs: ["routerLinkActive"] }], pipes: { "async": i4.AsyncPipe, "mheTranslate": i1.TranslatePipe }, animations: [
            trigger('optionSelection', [
                state('selected', style({
                    backgroundColor: '#e0e7e9',
                })),
                transition('* => selected', [
                    animate(`${DROPDOWN_SELECTED_STATE_TIME}ms`, keyframes([
                        style({ backgroundColor: 'white', offset: 0 }),
                        style({ backgroundColor: '#e0e7e9', offset: 1.0 }),
                    ])),
                ]),
            ]),
            trigger('hideDropdown', [
                state('dropdownHidden', style({ opacity: 0 })),
                transition('* => dropdownHidden', animate(`${DROPDOWN_SELECTED_STATE_TIME}ms`)),
            ]),
        ], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: DropdownComponent, decorators: [{
            type: Component,
            args: [{ providers: [provideValueAccessor(DropdownComponent)], selector: 'mhe-dropdown', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, animations: [
                        trigger('optionSelection', [
                            state('selected', style({
                                backgroundColor: '#e0e7e9',
                            })),
                            transition('* => selected', [
                                animate(`${DROPDOWN_SELECTED_STATE_TIME}ms`, keyframes([
                                    style({ backgroundColor: 'white', offset: 0 }),
                                    style({ backgroundColor: '#e0e7e9', offset: 1.0 }),
                                ])),
                            ]),
                        ]),
                        trigger('hideDropdown', [
                            state('dropdownHidden', style({ opacity: 0 })),
                            transition('* => dropdownHidden', animate(`${DROPDOWN_SELECTED_STATE_TIME}ms`)),
                        ]),
                    ], template: "<div\n  class=\"dropdown ahe-ui-dropdown\"\n  [class.btn-group]=\"splitActionText\"\n  (click)=\"_onTouch()\"\n  (keyup)=\"_onTouch()\"\n>\n  <ng-container *ngIf=\"!splitActionText; else splitButton\">\n    <button\n      *ngIf=\"!icon && !image; else iconButton\"\n      [id]=\"templateUuids.get('buttonId')\"\n      type=\"button\"\n      [attr.disabled]=\"disabledProp(disabled)\"\n      [attr.aria-expanded]=\"isExpanded\"\n      class=\"btn btn-secondary dropdown-btn\"\n      [ngClass]=\"{ placeholder: hasPlaceholderText(selected), open: isExpanded }\"\n      (click)=\"toggleMenu($event)\"\n      [ngStyle]=\"{ 'width.px': buttonWidth }\"\n      #controlButton\n    >\n      <span class=\"sr-only\">{{ toggleButtonAriaLabel }}</span>\n      <span>{{ currentSelection }}</span>\n      <i class=\"dpg-icon dpg-icon-dropdown-arrow-down\"></i>\n    </button>\n    <ng-template #iconButton>\n      <button\n        [id]=\"templateUuids.get('buttonId')\"\n        type=\"button\"\n        class=\"btn btn-icon ngx-shared\"\n        [ngClass]=\"{ 'btn-padding': image }\"\n        (click)=\"toggleMenu($event)\"\n        [attr.disabled]=\"disabledProp(disabled)\"\n        aria-haspopup=\"listbox\"\n        [attr.aria-expanded]=\"isExpanded\"\n        [ngStyle]=\"{ 'width.px': 36 }\"\n        #toggleButtonWithIcon\n      >\n        <span class=\"sr-only\">{{ toggleButtonAriaLabel }}</span>\n        <img *ngIf=\"image\" class=\"ahe-icon avatar-image\" [src]=\"image\" />\n        <i *ngIf=\"icon && !image\" class=\"ahe-icon {{ icon }}\"></i>\n      </button>\n    </ng-template>\n  </ng-container>\n</div>\n\n<ng-template cdkPortal #dropdownMenuPortal=\"cdkPortal\">\n  <div\n    class=\"ahe-ui-dropdown\"\n    [@hideDropdown]=\"!multiple && triggerHideDropdown ? 'dropdownHidden' : ''\"\n    (@hideDropdown.done)=\"hideDropdownAnimationDone($event)\"\n  >\n    <ul\n      role=\"listbox\"\n      (scroll)=\"onScroll($event)\"\n      (keydown)=\"onKey($event)\"\n      [attr.aria-expanded]=\"isExpanded\"\n      [attr.aria-label]=\"listboxLabel\"\n      class=\"dropdown-menu list-items-container\"\n      [class.dropdown-menu-right]=\"optionPosition === 'left'\"\n    >\n      <li *ngIf=\"filterable || searchable\" class=\"search-field\">\n        <div class=\"ahe-ui form-group dropdown-menu-search\">\n          <input\n            type=\"text\"\n            autocomplete=\"off\"\n            class=\"form-control search\"\n            (blur)=\"searchIsFocused = false\"\n            (focus)=\"searchIsFocused = true\"\n            placeholder=\"{{ 'ngx-shared-search' | mheTranslate | async }}\"\n            (keyup)=\"searchOptions($event)\"\n          />\n        </div>\n      </li>\n      <li *ngIf=\"multiple && selectAll\" (click)=\"selectAllOptions()\" role=\"option\">\n        <label\n          class=\"ahe-ui-checkbox dropdown-item\"\n          attr.aria-label=\"{{ 'ngx-shared-select-all' | mheTranslate | async }} {{\n            'ngx-shared-menu-option' | mheTranslate | async\n          }}\"\n        >\n          <input type=\"checkbox\" role=\"menuitemcheckbox\" [checked]=\"allSelected\" />\n          <span aria-hidden=\"true\">{{ 'ngx-shared-select-all' | mheTranslate | async }}</span>\n        </label>\n      </li>\n      <li\n        *ngFor=\"let option of options; trackBy: trackById; let i = index\"\n        #listItem\n        role=\"presentation\"\n        [attr.aria-disabled]=\"option.disabled\"\n        [class.checkmark]=\"!multiple && containsOption(option, selected)\"\n        [class.disabled]=\"option.disabled\"\n        [class.dpg-icon-confirmation]=\"!multiple && containsOption(option, selected)\"\n        [class.option-group-options]=\"_isChild[i]\"\n        [class.option-group-title]=\"!!option.children\"\n        (click)=\"selectOption(option)\"\n      >\n        <ng-container *ngTemplateOutlet=\"optionTemplate; context: getContext(i, option)\"></ng-container>\n      </li>\n    </ul>\n  </div>\n</ng-template>\n\n<ng-template\n  #optionContent\n  let-viewValue=\"viewValue\"\n  let-viewIcons=\"viewIcons\"\n  let-viewDescription=\"viewDescription\"\n>\n  <span aria-hidden=\"true\" [ngClass]=\"{ 'view-title': viewDescription }\">\n    <span>{{ viewValue }}</span>\n    <span class=\"view-icons\">\n      <i\n        *ngFor=\"let viewIcon of viewIcons\"\n        class=\"dpg-icon {{ viewIcon.class }}\"\n        title=\"{{ viewIcon.label }}\"\n      ></i>\n    </span>\n  </span>\n  <p class=\"view-description\" *ngIf=\"viewDescription\" aria-hidden=\"true\">{{ viewDescription }}</p>\n</ng-template>\n\n<ng-template #optionTemplate let-option=\"option\" let-isChild=\"isChild\" let-isHeader=\"option.children\">\n  <label *ngIf=\"multiple\" [attr.aria-label]=\"getOptionLabel(option)\" class=\"ahe-ui-checkbox dropdown-item\">\n    <input\n      type=\"checkbox\"\n      role=\"menuitemcheckbox\"\n      [value]=\"option.value\"\n      [checked]=\"containsOption(option, selected)\"\n    />\n    <ng-container *ngTemplateOutlet=\"optionContent; context: option\"></ng-container>\n  </label>\n  <ng-container *ngIf=\"!multiple\">\n    <a\n      *ngIf=\"(!option.routerLink && !option.href) || option.disabled\"\n      href\n      role=\"option\"\n      [attr.aria-label]=\"getOptionLabel(option)\"\n      [@optionSelection]=\"\n        this.triggerOptionSelectionAnimation && containsOption(option, selected) ? 'selected' : ''\n      \"\n      (@optionSelection.done)=\"optionSelectionAnimationDone($event)\"\n      class=\"dropdown-item\"\n      [class.is-header]=\"isHeader || null\"\n      [attr.tabindex]=\"!option.disabled && !option.children ? 0 : null\"\n      (click)=\"linkClickHandler($event, option)\"\n      (focus)=\"linkFocusHandler($event, option)\"\n    >\n      <ng-container *ngTemplateOutlet=\"optionContent; context: option\"></ng-container>\n    </a>\n    <a\n      *ngIf=\"option.href\"\n      [@optionSelection]=\"\n        this.triggerOptionSelectionAnimation && containsOption(option, selected) ? 'selected' : ''\n      \"\n      (@optionSelection.done)=\"optionSelectionAnimationDone($event)\"\n      class=\"dropdown-item ahe-ui-link\"\n      [class.is-header]=\"isHeader || null\"\n      (click)=\"linkClickHandler($event, option)\"\n      (focus)=\"linkFocusHandler($event, option)\"\n      href=\"{{ option.href }}\"\n      target=\"{{ option.target }}\"\n    >\n      <ng-container *ngTemplateOutlet=\"optionContent; context: option\"></ng-container>\n    </a>\n    <a\n      *ngIf=\"option.routerLink\"\n      [@optionSelection]=\"\n        this.triggerOptionSelectionAnimation && containsOption(option, selected) ? 'selected' : ''\n      \"\n      (@optionSelection.done)=\"optionSelectionAnimationDone($event)\"\n      class=\"dropdown-item ahe-ui-link\"\n      [class.is-header]=\"isHeader || null\"\n      (click)=\"linkClickHandler($event, option)\"\n      (focus)=\"linkFocusHandler($event, option)\"\n      routerLinkActive=\"active\"\n      routerLink=\"{{ option.routerLink }}\"\n    >\n      <ng-container *ngTemplateOutlet=\"optionContent; context: option\"></ng-container>\n    </a>\n  </ng-container>\n</ng-template>\n\n<ng-template #splitButton>\n  <button\n    [id]=\"templateUuids.get('buttonId')\"\n    class=\"btn btn-secondary\"\n    (click)=\"splitActionClick.emit($event)\"\n    #splitActionButton\n  >\n    {{ splitActionText }}\n  </button>\n  <button\n    type=\"button\"\n    [attr.disabled]=\"disabledProp(disabled)\"\n    [attr.aria-expanded]=\"isExpanded\"\n    class=\"btn btn-icon dpg-input-group-btn\"\n    [ngClass]=\"{ placeholder: hasPlaceholderText(selected), open: isExpanded }\"\n    (click)=\"toggleMenu($event)\"\n    #controlButton\n  >\n    <i class=\"dpg-icon dpg-icon-dropdown-arrow-down\">\n      <span class=\"sr-only\">{{ toggleButtonAriaLabel }}</span>\n    </i>\n  </button>\n</ng-template>\n", styles: ["mhe-dropdown{display:inline-block}.btn-padding{width:36px !important;padding:0 !important}.avatar-image{width:35px;height:33px;border-radius:4px}/*# sourceMappingURL=dropdown.component.css.map */\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i1.TranslateService }, { type: i2.Overlay }, { type: i3.DropdownService }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { minWidth: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], splitActionText: [{
                type: Input
            }], disabled: [{
                type: Input
            }], image: [{
                type: Input
            }], optionPosition: [{
                type: Input
            }], toggleButtonAriaLabel: [{
                type: Input
            }], listboxLabel: [{
                type: Input
            }], selected: [{
                type: Input
            }], isMenu: [{
                type: Input
            }], icon: [{
                type: Input
            }], multiple: [{
                type: Input
            }], filterable: [{
                type: Input
            }], syncWidths: [{
                type: Input
            }], buttonWidth: [{
                type: Input
            }], searchable: [{
                type: Input
            }], selectAll: [{
                type: Input
            }], selectAllText: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], panelClass: [{
                type: Input
            }], closeOnBlur: [{
                type: Input
            }], options: [{
                type: Input
            }], value: [{
                type: Input
            }], selectionChange: [{
                type: Output
            }], searchTerm: [{
                type: Output
            }], scrollBottomReached: [{
                type: Output
            }], close: [{
                type: Output
            }], splitActionClick: [{
                type: Output
            }], controlButton: [{
                type: ViewChild,
                args: ['controlButton']
            }], splitActionButton: [{
                type: ViewChild,
                args: ['splitActionButton']
            }], toggleButtonWithIcon: [{
                type: ViewChild,
                args: ['toggleButtonWithIcon']
            }], dropdownMenuPortal: [{
                type: ViewChild,
                args: ['dropdownMenuPortal', { static: true }]
            }] } });
